import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { WikiTree, filterWikisForScope } from "@/components/kb/WikiTree";
import {
  AdminAlert,
  AdminEmptyState,
  AdminList,
  AdminListRow,
  AdminPageHeader,
  AdminPill,
} from "@/components/shell/AdminPage";
import { ApiError } from "@/lib/api";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { createKbApi, type KbRevision } from "@/lib/kb-api";

export default function KbAuditPage() {
  const { accountId = "" } = useParams();
  const { modules } = useAccountWorkspace();
  const kb = useMemo(() => createKbApi(accountId), [accountId]);

  const [revisions, setRevisions] = useState<KbRevision[]>([]);
  const [selectedWikiId, setSelectedWikiId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRevisions = useCallback(
    async (wikiId: number | null) => {
      try {
        const payload = wikiId
          ? await kb.listRevisions({ wiki_id: wikiId })
          : await kb.listRevisions();
        setRevisions(payload.revisions);
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : "无法加载版本记录");
      }
    },
    [kb],
  );

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const wikiPayload = await kb.listWikis();
      const scoped = filterWikisForScope(wikiPayload.wikis, "enterprise");
      const wikiId =
        selectedWikiId && scoped.some((wiki) => wiki.id === selectedWikiId)
          ? selectedWikiId
          : scoped[0]?.id ?? null;
      if (wikiId && wikiId !== selectedWikiId) setSelectedWikiId(wikiId);
      await loadRevisions(wikiId);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "无法加载审计数据");
    } finally {
      setIsLoading(false);
    }
  }, [kb, selectedWikiId, loadRevisions]);

  useEffect(() => {
    void load();
  }, [load]);

  const kbEnabled = modules.some((module) => module.key === "kb" && module.enabled);

  return (
    <div className="grid min-h-0 flex-1 grid-cols-[minmax(220px,280px)_1fr] overflow-hidden rounded-lg border border-border">
      <aside className="border-r border-border bg-muted/20">
        <WikiTree accountId={accountId} scope="enterprise" />
      </aside>

      <div className="space-y-6 overflow-y-auto p-6">
        <AdminPageHeader
          title="审计 / 版本"
          description="知识条目 revision 记录，含 actor 与 source（可溯源）。"
        />

        {!kbEnabled ? (
          <AdminAlert tone="info">
            当前账户未订阅知识库。
            <Link to={`/accounts/${accountId}/subscriptions`} className="ml-1 underline">
              前往订阅
            </Link>
          </AdminAlert>
        ) : null}

        {error ? <AdminAlert>{error}</AdminAlert> : null}

        {isLoading ? (
          <p className="text-sm text-muted-foreground">正在加载…</p>
        ) : revisions.length === 0 ? (
          <AdminEmptyState>暂无版本记录</AdminEmptyState>
        ) : (
          <AdminList>
            {revisions.map((revision) => (
              <AdminListRow key={revision.id}>
                <div className="min-w-0 space-y-1">
                  <div className="font-medium">
                    {revision.page_title ?? `Page #${revision.page_id}`}
                    {revision.page_slug ? (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">{revision.page_slug}</span>
                    ) : null}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {revision.actor_type} #{revision.actor_id} · {revision.source}
                    {revision.source_ref ? ` · ${revision.source_ref}` : ""}
                    {" · "}
                    {new Date(revision.created_at).toLocaleString()}
                  </div>
                </div>
                <AdminPill tone="muted">rev #{revision.id}</AdminPill>
              </AdminListRow>
            ))}
          </AdminList>
        )}
      </div>
    </div>
  );
}
